/**
 * Sprint 30 M1 — Policy Library server fns.
 *
 * The Library reads from the canonical `policy_registry` table, which
 * every capability writes to via triggers. Capability tables remain
 * authoritative for execution; the registry is the unified index that
 * powers `/app/policies`, search, lifecycle, and Policy Intelligence.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { runHoldRulePreview } from "@/lib/hold-rules.functions";
import { evaluate, buildOrderContext, type Condition } from "@/lib/policy-conditions";

export type PolicyLibraryRow = {
  id: string;
  policy_id: string;
  tenant_id: string;
  title: string;
  summary: string | null;
  status: "active" | "paused" | "draft" | "proposed" | "archived";
  scope_level: "tenant" | "customer" | "facility";
  customer_id: string | null;
  facility_id: string | null;
  primary_capability: string;
  canonical_targets: Array<{ capability: string; table: string; id: string }>;
  source: "manual" | "ai_proposal" | "ai_proposal_modified" | "imported";
  intent_id: string | null;
  intent_prompt: string | null;
  author_id: string | null;
  created_at: string;
  updated_at: string;
  last_evaluated_at: string | null;
  last_triggered_at: string | null;
  trigger_count_30d: number;
  customer_name?: string | null;
};

const ListInput = z.object({
  tenantId: z.string().uuid(),
  q: z.string().trim().max(200).optional(),
  capability: z.string().max(64).optional(),
  status: z.enum(["active", "paused", "draft", "proposed", "archived"]).optional(),
  customerId: z.string().uuid().optional(),
  notFiredDays: z.number().int().min(1).max(365).optional(),
  modifiedSinceDays: z.number().int().min(1).max(365).optional(),
  limit: z.number().int().min(1).max(500).default(200),
});

export const listPolicies = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => ListInput.parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    // Ranked search via canonical RPC. The RPC handles every filter
    // (q, capability, status, customer, not-fired, modified) and joins
    // customer name into the relevance pool so phrases like a customer
    // name surface that customer's policies first.
    const { data: rows, error } = await supabase.rpc("search_policy_registry", {
      p_tenant_id: data.tenantId,
      p_q: data.q ?? null,
      p_capability: data.capability ?? null,
      p_status: data.status ?? null,
      p_customer_id: data.customerId ?? null,
      p_not_fired_days: data.notFiredDays ?? null,
      p_modified_since_days: data.modifiedSinceDays ?? null,
      p_limit: data.limit,
    });
    if (error) throw new Error(error.message);

    const out: PolicyLibraryRow[] = (rows ?? []).map((r: any) => ({
      ...r,
      canonical_targets: Array.isArray(r.canonical_targets) ? r.canonical_targets : [],
    }));
    return { rows: out };
  });

export const getPolicy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ policyId: z.string().min(1) }).parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    // Accept either the human `policy_id` (POL-…) or the UUID `id`.
    const isUuid = /^[0-9a-f-]{36}$/i.test(data.policyId);
    const col = isUuid ? "id" : "policy_id";
    const { data: row, error } = await supabase
      .from("policy_registry")
      .select("*, customers(name)")
      .eq(col, data.policyId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("policy not found");
    return {
      policy: {
        ...row,
        canonical_targets: Array.isArray(row.canonical_targets) ? row.canonical_targets : [],
        customer_name: row.customers?.name ?? null,
      } as PolicyLibraryRow,
    };
  });

export const countPoliciesByCapability = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ tenantId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await (context.supabase as any)
      .from("policy_registry")
      .select("primary_capability, status")
      .eq("tenant_id", data.tenantId);
    if (error) throw new Error(error.message);
    const byCapability = new Map<string, { total: number; active: number }>();
    let active = 0;
    for (const r of rows ?? []) {
      const k = r.primary_capability as string;
      const slot = byCapability.get(k) ?? { total: 0, active: 0 };
      slot.total += 1;
      if (r.status === "active") {
        slot.active += 1;
        active += 1;
      }
      byCapability.set(k, slot);
    }
    return {
      total: (rows ?? []).length,
      active,
      capabilities: Array.from(byCapability.entries()).map(([key, v]) => ({ key, ...v })),
    };
  });

/* ---------------------------------------------------------------------------
 * Sprint 30 M3 — Policy Impact + Recent Decisions
 *
 * Both reads use the capability's canonical evaluator path — never a parallel
 * UI re-implementation. The registry is the index; capability tables remain
 * the execution source of truth.
 * ------------------------------------------------------------------------- */

export type PolicyPreview =
  | {
      kind: "hold_release";
      matching_now: number;
      matching_historical_90d: number;
      examples: Array<{ id: string; label: string; reason: string; href: string }>;
    }
  | {
      kind: "hold_creation";
      sample_size: number;
      matching_in_sample: number;
      match_rate: number;
      examples: Array<{ id: string; label: string; reason: string; href: string }>;
    }
  | { kind: "unavailable"; reason: string };

export type PolicyDecision = {
  id: string;
  at: string;
  summary: string;
  detail?: string;
  href?: string;
  tone: "ok" | "warn" | "info";
};

export const getPolicyImpact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ policyId: z.string().min(1) }).parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const isUuid = /^[0-9a-f-]{36}$/i.test(data.policyId);
    const col = isUuid ? "id" : "policy_id";
    const { data: reg, error: regErr } = await supabase
      .from("policy_registry")
      .select("id, tenant_id, primary_capability, canonical_targets, customer_id, scope_level")
      .eq(col, data.policyId)
      .maybeSingle();
    if (regErr) throw new Error(regErr.message);
    if (!reg) throw new Error("policy not found");

    const targets: Array<{ capability: string; table: string; id: string }> =
      Array.isArray(reg.canonical_targets) ? reg.canonical_targets : [];
    const primary = targets.find((t) => t.capability === reg.primary_capability) ?? targets[0];

    let preview: PolicyPreview = { kind: "unavailable", reason: "No canonical target." };
    const decisions: PolicyDecision[] = [];

    if (primary?.capability === "hold_release" && primary.table === "hold_release_rules") {
      const { data: rule } = await supabase
        .from("hold_release_rules")
        .select("id, hold_reason, trigger, params, scope_level, customer_id")
        .eq("id", primary.id)
        .maybeSingle();
      if (rule) {
        const scope =
          rule.scope_level === "customer" && rule.customer_id
            ? { scope_level: "customer" as const, customer_id: rule.customer_id }
            : { scope_level: "tenant" as const, customer_id: null };
        try {
          const r = await runHoldRulePreview(supabase, {
            tenantId: reg.tenant_id,
            holdReason: rule.hold_reason,
            trigger: rule.trigger,
            params: rule.params ?? {},
            scope,
          });
          preview = {
            kind: "hold_release",
            matching_now: r.matchingNow,
            matching_historical_90d: r.matchingHistorical,
            examples: r.examples.map((e: any) => ({
              id: e.id, label: e.label, reason: e.reason, href: e.href,
            })),
          };
        } catch (e: any) {
          preview = { kind: "unavailable", reason: e?.message ?? "Preview failed." };
        }
        // Recent decisions: place + release events on this reason in scope.
        let dq = supabase
          .from("order_holds")
          .select("id, order_id, placed_at, released_at, released_reason, notes, orders!inner(order_number, customer_id)")
          .eq("tenant_id", reg.tenant_id)
          .eq("reason", rule.hold_reason)
          .order("placed_at", { ascending: false })
          .limit(15);
        if (scope.scope_level === "customer") dq = dq.eq("orders.customer_id", scope.customer_id);
        const { data: hRows } = await dq;
        for (const r of (hRows ?? []) as any[]) {
          const orderNumber = r.orders?.order_number ?? r.order_id?.slice(0, 8);
          decisions.push({
            id: `${r.id}:placed`,
            at: r.placed_at,
            summary: `Hold placed on Order ${orderNumber}`,
            detail: r.notes ?? undefined,
            href: `/app/orders/${r.order_id}`,
            tone: "warn",
          });
          if (r.released_at) {
            decisions.push({
              id: `${r.id}:released`,
              at: r.released_at,
              summary: `Hold released on Order ${orderNumber}`,
              detail: r.released_reason ?? undefined,
              href: `/app/orders/${r.order_id}`,
              tone: "ok",
            });
          }
        }
      }
    } else if (primary?.capability === "hold_creation" && primary.table === "hold_creation_rules") {
      const { data: rule } = await supabase
        .from("hold_creation_rules")
        .select("id, name, scope_level, customer_id, condition_tree, actions")
        .eq("id", primary.id)
        .maybeSingle();
      if (rule) {
        const tree = rule.condition_tree as Condition;
        const sampleSize = 300;
        let q = supabase
          .from("orders")
          .select("id, order_number, customer_id, created_at")
          .eq("tenant_id", reg.tenant_id)
          .order("created_at", { ascending: false })
          .limit(sampleSize);
        if (rule.scope_level === "customer" && rule.customer_id) {
          q = q.eq("customer_id", rule.customer_id);
        }
        const { data: orders } = await q;
        const examples: Array<{ id: string; label: string; reason: string; href: string }> = [];
        let evaluated = 0;
        let matchCount = 0;
        for (const o of (orders ?? []) as any[]) {
          const ctx = await buildOrderContext(supabase, "order.imported", o.id);
          if (!ctx) continue;
          evaluated++;
          try {
            if (evaluate(tree, ctx)) {
              matchCount++;
              if (examples.length < 5) {
                examples.push({
                  id: o.id,
                  label: o.order_number ?? o.id.slice(0, 8),
                  reason: "Matches condition",
                  href: `/app/orders/${o.id}`,
                });
              }
            }
          } catch { /* skip malformed */ }
        }
        preview = {
          kind: "hold_creation",
          sample_size: evaluated,
          matching_in_sample: matchCount,
          match_rate: evaluated > 0 ? matchCount / evaluated : 0,
          examples,
        };

        // Recent decisions: holds whose notes/actions match this rule —
        // proxy via reason emitted by any of the rule's actions.
        const reasons = Array.isArray(rule.actions)
          ? Array.from(new Set((rule.actions as any[]).map((a) => a?.reason).filter(Boolean)))
          : [];
        if (reasons.length > 0) {
          let dq = supabase
            .from("order_holds")
            .select("id, order_id, placed_at, reason, notes, orders!inner(order_number, customer_id)")
            .eq("tenant_id", reg.tenant_id)
            .in("reason", reasons)
            .order("placed_at", { ascending: false })
            .limit(15);
          if (rule.scope_level === "customer" && rule.customer_id) {
            dq = dq.eq("orders.customer_id", rule.customer_id);
          }
          const { data: hRows } = await dq;
          for (const r of (hRows ?? []) as any[]) {
            const orderNumber = r.orders?.order_number ?? r.order_id?.slice(0, 8);
            decisions.push({
              id: `${r.id}:placed`,
              at: r.placed_at,
              summary: `Hold placed on Order ${orderNumber} (${r.reason})`,
              detail: r.notes ?? undefined,
              href: `/app/orders/${r.order_id}`,
              tone: "warn",
            });
          }
        }
      }
    } else {
      preview = {
        kind: "unavailable",
        reason: "Preview not yet available for this capability.",
      };
    }

    decisions.sort((a, b) => (a.at < b.at ? 1 : -1));
    return { preview, decisions: decisions.slice(0, 10) };
  });